import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/Button';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFinderApi } from './useFinderApi';
import { FaEdit } from 'react-icons/fa';

const RELATIONSHIP_OPTIONS = [
  { value: "self", label: "Self" },
  { value: "spouse_partner", label: "Spouse/Partner" },
  { value: "child", label: "Child" },
  { value: "stepchild", label: "Stepchild" },
  { value: "other_relative", label: "Other Relative" },
  { value: "non_relative", label: "Non-Relative" }
];

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "nonbinary", label: "Nonbinary" },
  { value: "transgender_male", label: "Transgender Male" },
  { value: "transgender_female", label: "Transgender Female" }
];

const UpdateNeed = () => {
  const {getNeedById, updateNeedForId, searchSpaces} = useFinderApi();

  const [referenceIdentifier, setReferenceIdentifier] = useState("");
  const [spaceTypeCategories, setSpaceTypeCategories] = useState([]);
  const [numPeople, setNumPeople] = useState(1);
  const [people, setPeople] = useState([{ age: '', gender: '', relationship: 'self' }]);
  const [ageErrors, setAgeErrors] = useState([]);
  const [isCaPc290, setIsCaPc290] = useState(null);
  const [isArsonHistory, setIsArsonHistory] = useState(null);
  const [numPets, setNumPets] = useState('');
  const [currentRegion, setCurrentRegion] = useState('');
  
  const [status, setStatus] = useState(''); // For status information

  const [needId, setNeedId] = useState(null); 
  const [loading, setLoading] = useState(true); 

  const [searchResultsCount, setSearchResultsCount] = useState(null); 
  const [searchComplete, setSearchComplete] = useState(false); 

  const [formComplete, setFormComplete] = useState(false);
  const [editingReference, setEditingReference] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { isNewNeed } = location.state || {};
  const { id } = useParams(); // For editing existing items

  const initialRender = useRef(true);

  useEffect(() => {
    if (id) {
      // get saved need
      getNeedById(id).then((need) => {
        setNeedId(need.id);
        setReferenceIdentifier(need.reference_identifier);
        setSpaceTypeCategories((need.space_type_categories == null) ? [] : need.space_type_categories);

        // Use people array for age, gender, relationship
        if (Array.isArray(need.people) && need.people.length > 0) {
          setPeople(need.people);
          setNumPeople(need.people.length); // <-- Set the dropdown to the actual number of people
        } else {
          setPeople([{ age: '', gender: '', relationship: 'self' }]);
          setNumPeople(1); // fallback
        }

        if (need.behavioral_history == null) {
          setIsCaPc290("");
          setIsArsonHistory("");
        }
        else {
          setIsCaPc290(need.behavioral_history.includes("ca_pc_290") ? "Yes" : "No");
          setIsArsonHistory(need.behavioral_history.includes("arson") ? "Yes" : "No");
        }
        
        setNumPets((need.num_pets == null) ? '' : need.num_pets.toString() );
        setCurrentRegion((need.current_region == null) ? '' : need.current_region);

        setLoading(false); 

      }).catch((error) => {
        setStatus('Error loading need details: ' + error.message);
      });
    } 
  }, [id, isNewNeed, getNeedById]);

  // TODO: Refactor. This is a hack to avoid having an array in the dependency array in the useEffect below. It results in a warning, which I am irresponsibly telling lint to ignore
  const stringifiedSpaceTypeCategories = JSON.stringify(spaceTypeCategories);

  useEffect(() => {
    const getNeedObjectFromFormState = () => {
      let need = {};
      need.reference_identifier = referenceIdentifier.trim();
      need.space_type_categories = (spaceTypeCategories && spaceTypeCategories.length > 0) ? spaceTypeCategories : null;
      need.current_region = currentRegion ? currentRegion : null;
      // Use people array for age, gender, relationship
      need.people = people.map((person, idx) => ({
        age: person.age && String(person.age).trim() && !isNaN(Number(person.age)) ? Number(person.age) : null,
        gender: person.gender ? person.gender : null,
        relationship: idx === 0 ? 'self' : (person.relationship ? person.relationship : null)
      }));
      need.num_pets = (numPets === "") ? null : Number(numPets);

      need.behavioral_history = null;
      if (isCaPc290) {
        if (need.behavioral_history === null) {need.behavioral_history = []};
        if (isCaPc290 === "Yes") {need.behavioral_history.push("ca_pc_290");}
      }
      if (isArsonHistory) {
        if (need.behavioral_history === null) {need.behavioral_history = []};
        if (isArsonHistory === "Yes") {need.behavioral_history.push("arson");}
      }
      
      return need;
    }
    async function updateAndSearch() {
      // TO DO: Fix this
      if (!loading) {
        // only update to server if not the initial render
        if (!initialRender.current) {
          const need = getNeedObjectFromFormState();
          await updateNeedForId(needId, need); // TODO check for error
        }
        else {
          initialRender.current = false;
        }
        
        // validate that the form is complete
        if (referenceIdentifier.trim().length > 0 
          && (spaceTypeCategories && spaceTypeCategories.length > 0)
          && people.every(person => person.age !== "" && person.gender && person.relationship)
          && isCaPc290
          && isArsonHistory
          && (numPets !== "")
          && currentRegion
        ) {
          setFormComplete(true);
        }
        else {
          setFormComplete(false);
        }

        const results = await searchSpaces(needId);
        setSearchResultsCount(results.matching_spaces);
        setSearchComplete(results.query_complete)
        
      }
    }
    updateAndSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, needId, referenceIdentifier, stringifiedSpaceTypeCategories, people, currentRegion, isCaPc290, isArsonHistory, numPets, searchSpaces, updateNeedForId]);    

  const handleSpaceTypeCategoriesChange = (e) => {
    const value = e.target.value;
  
    if (e.target.checked) {
      // If the checkbox is checked, add the value to the array if it's not already there
      setSpaceTypeCategories((prevCategories) => [...prevCategories, value]);
    } else {
      // If the checkbox is unchecked, remove the value from the array
      setSpaceTypeCategories((prevCategories) => prevCategories.filter((option) => option !== value));
    }
  };

  // Adjust people array when numPeople changes
  useEffect(() => {
    setPeople(prev => {
      let arr = [...prev];
      if (arr.length < numPeople) {
        for (let i = arr.length; i < numPeople; i++) {
          arr.push({ age: '', gender: '', relationship: '' });
        }
      } else if (arr.length > numPeople) {
        arr = arr.slice(0, numPeople);
      }
      if (arr.length > 0) arr[0].relationship = 'self';
      return arr;
    });
  }, [numPeople]);

  // Age validation and update on blur (only update age if valid)
  const handlePersonAgeBlur = (idx, value) => {
    setAgeErrors(prev => {
      const arr = [...prev];
      if (value === '' || (/^\d+$/.test(value) && value >= 0 && value <= 120)) {
        arr[idx] = '';
        // Only update the age in people if valid
        setPeople(prevPeople => {
          const updated = [...prevPeople];
          updated[idx] = { ...updated[idx], age: value };
          return updated;
        });
      } else {
        arr[idx] = 'Please enter a valid age (0-120).';
      }
      return arr;
    });
  };

  // Handle person field changes (for gender and relationship only)
  const handlePersonChange = (idx, field, value) => {
    setPeople(prev => {
      const arr = [...prev];
      arr[idx] = { ...arr[idx], [field]: value };
      if (idx === 0) arr[0].relationship = 'self';
      return arr;
    });
  };

  const handleBackClick = () => {
    navigate('/'); // Navigate back to the item list page
  };

  const handleViewSearchResults = () => {
    // validate for required fields
    navigate(`/needs/${id}/search-results`); // Navigate to the search results page
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Sticky Header with Back Button and Title */}
      <div className="sticky top-0 z-10 bg-sr-slate-100 border-b border-gray-200 px-2 py-1 flex items-center justify-center shadow-md" style={{ minHeight: "40px" }}>
        <button
          onClick={handleBackClick}
          className="text-blue-600 text-lg absolute left-2"
          style={{ minWidth: "70px" }}
        >
          ← Back
        </button>
        <span className="text-base font-bold text-sr-darkblue">Shelter Ready</span>
      </div>

      {/* Add padding only to the scrollable content */}
      <div className="flex-grow overflow-auto px-4 sm:px-8 md:px-16 lg:px-32 xl:px-64 pt-4 pb-48">
        <div>

          {/* Reference Identifier */}
          <div
            className={`mb-4 p-4 rounded-md flex items-center justify-between cursor-pointer transition-colors ${
              editingReference ? "bg-gray-200" : ""
            } hover:bg-slate-200`}
            onClick={() => {
              if (!editingReference) setEditingReference(true);
            }}
          >
            {!editingReference ? (
              <h1 className="text-2xl font-bold text-sr-darkblue flex items-center">
                <span>{referenceIdentifier || "Reference Identifier"}</span>
                <FaEdit
                  className="ml-2 text-slate-400 text-base"
                  
                  aria-label="Edit Reference Identifier"
                  tabIndex={-1}
                />
              </h1>
            ) : (
              <div className="w-full flex flex-col gap-2" onClick={e => e.stopPropagation()}>
                <label className="block text-lg font-semibold text-gray-900 mb-2" htmlFor="reference-identifier">
                  <p className="mb-2">Reference Identifier</p>
                  <p className="text-xs text-gray-500">The details of this search are saved under the following label. This is for your reference only.</p>
                </label>
                <div className="flex gap-2">
                  <input
                    id="reference-identifier"
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md text-lg"
                    value={referenceIdentifier}
                    onChange={e => setReferenceIdentifier(e.target.value)}
                    autoFocus
                    required
                    onBlur={() => setEditingReference(false)}
                  />
                </div>
              </div>
            )}
          </div>


          {/* Number of People in Household */}
          <div className="mb-8 p-4">
            <label className="block text-lg font-semibold text-gray-900 mb-4" htmlFor="num-people">
              <p className="mb-2">How many people need shelter?</p>
            </label>
            <select
              id="num-people"
              className="w-full px-4 py-3 border border-gray-300 rounded-md text-lg"
              value={numPeople}
              onChange={e => setNumPeople(Number(e.target.value))}
            >
              {[...Array(10)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i === 0
                    ? "Single individual"
                    : `${i + 1} person household`}
                </option>
              ))}
            </select>
          </div>

          {/* People in Household */}
          {people.map((person, idx) => (
            <div key={idx} className="mb-8 p-4 border border-gray-400 rounded-md">
              {people.length === 1 ? (
                <div className="font-semibold text-lg mb-2">
                  Individual Client
                </div>
              ) : (
                <div className="font-semibold text-lg mb-2">
                  {`Person ${idx + 1} ${idx === 0 ? '(Head of Household)' : ''}`}
                </div>
              )}
              
              {/* Age */}
              <div className="mb-4">
                <label className="block text-gray-900 mb-2" htmlFor={`age-${idx}`}>What is this person's age?</label>
                <input
                  id={`age-${idx}`}
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md text-lg"
                  defaultValue={person.age}
                  onBlur={e => handlePersonAgeBlur(idx, e.target.value)}
                />
                {ageErrors[idx] && <p className="text-red-600 mt-2">{ageErrors[idx]}</p>}
              </div>

              {/* Gender */}
              <div className="mb-4">
                <label className="block text-gray-900 mb-2" htmlFor={`gender-${idx}`}>What is this person's gender?</label>
                <select
                  id={`gender-${idx}`}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md text-lg"
                  value={person.gender || ""}
                  onChange={e => handlePersonChange(idx, 'gender', e.target.value)}
                >
                  <option value="" disabled hidden></option>
                  {GENDER_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Relationship (skip for first person) */}
              {idx !== 0 && (
                <div className="mb-4">
                  <label className="block text-gray-900 mb-2" htmlFor={`relationship-${idx}`}>What is this person's relationship to the head of household?</label>
                  <select
                    id={`relationship-${idx}`}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md text-lg"
                    value={person.relationship || ""}
                    onChange={e => handlePersonChange(idx, 'relationship', e.target.value)}
                  >
                    <option value="" disabled hidden></option>
                    {RELATIONSHIP_OPTIONS.filter(opt => opt.value !== "self").map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ))}

          {/* Space Type Categories - moved after people */}
          <div className="mb-4 p-4">
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              <p className="mb-2">What types of shelter spaces are appropriate for your client?</p>
              <p className="text-sm text-gray-500">Choose all that apply</p>
            </label>
            <div className="mb-4">
              <input
                type="checkbox"
                id="spacetypecategory-option-congregate"
                value="congregate"
                checked={spaceTypeCategories.includes('congregate')}
                onChange={handleSpaceTypeCategoriesChange}
              />
              <label className="ml-4 text-lg" htmlFor="spacetypecategory-option-congregate">Congregate</label>
            </div>
            <div className="mb-4">
              <input
                type="checkbox"
                id="spacetypecategory-option-semi-congregate"
                value="semi_congregate"
                checked={spaceTypeCategories.includes('semi_congregate')}
                onChange={handleSpaceTypeCategoriesChange}
              />
              <label className="ml-4 text-lg" htmlFor="spacetypecategory-option-semi-congregate">Semi-Congregate</label>
            </div>
            <div className="mb-4">
              <input
                type="checkbox"
                id="spacetypecategory-option-noncongregate"
                value="non_congregate"
                checked={spaceTypeCategories.includes('non_congregate')}
                onChange={handleSpaceTypeCategoriesChange}
              />
              <label className="ml-4 text-lg" htmlFor="spacetypecategory-option-noncongregate">Non-Congregate</label>
            </div>
            <div className="mb-4">
              <input
                type="checkbox"
                id="spacetypecategory-option-safeparking"
                value="safe_parking"
                checked={spaceTypeCategories.includes('safe_parking')}
                onChange={handleSpaceTypeCategoriesChange}
              />
              <label className="ml-4 text-lg" htmlFor="spacetypecategory-option-safeparking">Safe Parking Space</label>
            </div>
            <div className="mb-4">
              <input
                type="checkbox"
                id="spacetypecategory-option-safe_sleeping_program"
                value="safe_sleeping_program"
                checked={spaceTypeCategories.includes('safe_sleeping_program')}
                onChange={handleSpaceTypeCategoriesChange}
              />
              <label className="ml-4 text-lg" htmlFor="spacetypecategory-option-safe_sleeping_program">Safe Sleeping Program</label>
            </div>
          </div>

          {/* PC 290 */}
          <div className="mb-8 p-4">
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              {people.length > 1
                ? "Has anyone in this household been convicted of any offense that requires registration under California Penal Code 290?"
                : "Has your client been convicted of any offense that requires registration under California Penal Code 290?"}
            </label>
            <div className="mb-4">
              <input
                type="radio"
                id="pc290-option-yes"
                name="pc290"
                value="Yes"
                checked={isCaPc290 === 'Yes'}
                onChange={(e) => setIsCaPc290(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="pc290-option-yes">Yes</label>
            </div>
            <div className="mb-4">
              <input
                type="radio"
                id="pc290-option-no"
                name="pc290"
                value="No"
                checked={isCaPc290 === 'No'}
                onChange={(e) => setIsCaPc290(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="pc290-option-no">No</label>
            </div>
          </div>

          {/* arson */}
          <div className="mb-4 p-4">
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              {people.length > 1
                ? "Does anyone in this household have a history of committing arson?"
                : "Does your client have a history of committing arson?"}
            </label>
            <div className="mb-4">
              <input
                type="radio"
                id="arson-option-yes"
                name="arson"
                value="Yes"
                checked={isArsonHistory === 'Yes'}
                onChange={(e) => setIsArsonHistory(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="arson-option-yes">Yes</label>
            </div>
            <div className="">
              <input
                type="radio"
                id="arson-option-no"
                name="arson"
                value="No"
                checked={isArsonHistory === 'No'}
                onChange={(e) => setIsArsonHistory(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="arson-option-no">No</label>
            </div>
          </div>

          {/* Pets */}
          <div className="mb-4 p-4">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Does your client have a pet?</label>
            <div className="mb-4">
              <input
                type="radio"
                id="pets-option-none"
                name="numPets"
                value="0"
                checked={numPets === '0'}
                onChange={(e) => setNumPets(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="pets-option-none">No pets</label>
            </div>
            <div className="mb-4">
              <input
                type="radio"
                id="pets-option-1"
                name="numPets"
                value="1"
                checked={numPets === '1'}
                onChange={(e) => setNumPets(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="pets-option-1">1 pet</label>
            </div>
            <div className="mb-4">
              <input
                type="radio"
                id="pets-option-2"
                name="numPets"
                value="2"
                checked={numPets === '2'}
                onChange={(e) => setNumPets(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="pets-option-2">2 pets</label>
            </div>
            <div className="mb-4">
              <input
                type="radio"
                id="pets-option-3"
                name="numPets"
                value="3"
                checked={numPets === '3'}
                onChange={(e) => setNumPets(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="pets-option-3">3 pets</label>
            </div>
          </div>

          {/* Current Region */}
          <div className="mb-4 p-4">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Where is your client located?</label>
            <div className="mb-4">
              <input
                type="radio"
                id="currentregion-option-northcounty"
                name="currentregion"
                value="sdco_north_county"
                checked={currentRegion === 'sdco_north_county'}
                onChange={(e) => setCurrentRegion(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="currentregion-option-northcounty">North County</label>
            </div>
            <div className="mb-4">
              <input
                type="radio"
                id="currentregion-option-centralsandiego"
                name="currentregion"
                value="sdco_central_sandiego"
                checked={currentRegion === 'sdco_central_sandiego'}
                onChange={(e) => setCurrentRegion(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="currentregion-option-centralsandiego">Central San Diego</label>
            </div>
            <div className="mb-4">
              <input
                type="radio"
                id="currentregion-option-eastcounty"
                name="currentregion"
                value="sdco_east_county"
                checked={currentRegion === 'sdco_east_county'}
                onChange={(e) => setCurrentRegion(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="currentregion-option-eastcounty">East County</label>
            </div>
            <div className="mb-4">
              <input
                type="radio"
                id="currentregion-option-southbay"
                name="currentregion"
                value="sdco_south_bay"
                checked={currentRegion === 'sdco_south_bay'}
                onChange={(e) => setCurrentRegion(e.target.value)}
              />
              <label className="ml-4 text-lg" htmlFor="currentregion-option-southbay">South Bay</label>
            </div>
          </div>
        </div>
      </div>

      {status && (
        <div className="bg-yellow-100 text-yellow-900 bg-opacity-95 p-6 text-center w-full fixed bottom-0 left-0 right-0">
          {status}
        </div>
      )}

      {!status && searchResultsCount === 0 && (
        <div className="bg-red-200 text-red-900 bg-opacity-95 p-6 text-center w-full fixed bottom-0 left-0 right-0">
          There are no matching spaces
        </div>
      )}

      {!status && searchResultsCount && (!searchComplete || !formComplete) && (
        <div className="bg-yellow-100 text-yellow-900 bg-opacity-95 p-6 text-center w-full fixed bottom-0 left-0 right-0">
          <p className="font-bold mb-2">{`We are searching ${searchResultsCount} possible spaces`}</p>
          <p className="text-xs">Tell us about your client and we'll keep narrowing things down</p>
        </div>
      )}

      {!status && searchResultsCount && searchComplete && formComplete && (
        <div className={`${searchResultsCount ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'} bg-opacity-95 font-bold  p-6 text-center w-full fixed bottom-0 left-0 right-0`}>
          {`We found ${searchResultsCount} possible spaces`}

          {formComplete && (
            <div>
              <Button onClick={handleViewSearchResults} className="text-lg m-2 py-3 px-6">
                Review available spaces
              </Button>
            </div>
          )}
        </div>
      )} 
    </div>
  );
};

export default UpdateNeed;
